import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type OpenDrawerOptions = {
  scrollToList?: boolean;
};

type ConsolidatedDrawerContextValue<TSelectedId> = {
  selectedId: TSelectedId | null;
  isOpen: boolean;
  shouldScrollToList: boolean;
  openDrawer: (id: TSelectedId, options?: OpenDrawerOptions) => void;
  closeDrawer: () => void;
  onOpenChange: (open: boolean) => void;
};

const ConsolidatedDrawerContext =
  createContext<ConsolidatedDrawerContextValue<unknown> | null>(null);

type ConsolidatedDrawerProviderProps = {
  children: ReactNode;
};

export const ConsolidatedDrawerProvider = ({
  children,
}: ConsolidatedDrawerProviderProps) => {
  const [selectedId, setSelectedId] = useState<unknown>(null);
  const [shouldScrollToList, setShouldScrollToList] = useState(false);

  const openDrawer = useCallback(
    (id: unknown, options?: OpenDrawerOptions) => {
      setSelectedId(id);
      setShouldScrollToList(options?.scrollToList ?? false);
    },
    [],
  );

  const closeDrawer = useCallback(() => {
    setSelectedId(null);
    setShouldScrollToList(false);
  }, []);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeDrawer();
      }
    },
    [closeDrawer],
  );

  const value = useMemo<ConsolidatedDrawerContextValue<unknown>>(
    () => ({
      selectedId,
      isOpen: selectedId !== null,
      shouldScrollToList,
      openDrawer,
      closeDrawer,
      onOpenChange,
    }),
    [selectedId, shouldScrollToList, openDrawer, closeDrawer, onOpenChange],
  );

  return (
    <ConsolidatedDrawerContext.Provider value={value}>
      {children}
    </ConsolidatedDrawerContext.Provider>
  );
};

export const useConsolidatedDrawer = <TSelectedId,>() => {
  const context = useContext(ConsolidatedDrawerContext);

  if (!context) {
    throw new Error(
      "useConsolidatedDrawer must be used within a ConsolidatedDrawerProvider",
    );
  }

  return context as ConsolidatedDrawerContextValue<TSelectedId>;
};
